import React, { useState, useCallback, useMemo } from 'react';
import { ListBulletIcon } from '@heroicons/react/24/outline';
import { isToday, parseISO } from 'date-fns';
import { useRecallReportStore } from '@/stores';
import { useRecallReports } from '@/hooks';
import { BaseFilterSidebar } from '@/components/ui/BaseFilterSidebar';
import { RecallReportCalendar } from './RecallReportCalendar';
import { ConfirmDialog } from './ConfirmDialog';
import { toDateKey } from '@/utils/date-key';

const FEED_RULES = ['Client transfers', 'Min Qty'];

export const FilterSidebar: React.FC = () => {
  const counterparty = useRecallReportStore((state) => state.activeCounterparty);

  const setSelectedReportId = useRecallReportStore((state) => state.setSelectedReportId);
  const selectedDate = useRecallReportStore((state) => state.selectedDate);
  const setSelectedDate = useRecallReportStore((state) => state.setSelectedDate);
  const isEditing = useRecallReportStore((state) => state.isEditing);
  const hasChanges = useRecallReportStore((state) => state.hasChanges);
  const cancelEdit = useRecallReportStore((state) => state.cancelEdit);

  const { data: reports = [] } = useRecallReports({ counterparty });

  // Unsaved changes confirmation state
  type PendingNav = { type: 'today' } | { type: 'date'; date: string };
  const [pendingNav, setPendingNav] = useState<PendingNav | null>(null);
  const showUnsavedConfirm = pendingNav !== null;

  // Compute available dates (server already filtered by counterparty)
  const availableDates = useMemo(() => {
    return reports
      .map((r) => toDateKey(new Date(r.created_at)))
      .filter((v, i, a) => a.indexOf(v) === i)
      .sort();
  }, [reports]);

  // Check if today's report exists for current counterparty
  const hasTodayReport = useMemo(
    () => reports.some((r) => isToday(parseISO(r.created_at))),
    [reports],
  );

  // Direct date navigation (the actual logic)
  const handleDateSelectDirect = useCallback(
    (date: string | null) => {
      setSelectedDate(date);

      if (date === null) {
        const todayReport = reports.find((r) => isToday(parseISO(r.created_at)));
        if (todayReport) {
          setSelectedReportId(todayReport.id);
        }
      } else {
        const matchingReport = reports.find(
          (r) => toDateKey(new Date(r.created_at)) === date,
        );
        if (matchingReport) {
          setSelectedReportId(matchingReport.id);
        }
      }
    },
    [reports, setSelectedDate, setSelectedReportId],
  );

  // Guarded date selection that checks for unsaved changes
  const handleDateSelect = useCallback(
    (date: string | null) => {
      if (isEditing && hasChanges()) {
        setPendingNav(date === null ? { type: 'today' } : { type: 'date', date });
        return;
      }
      if (isEditing) {
        cancelEdit();
      }
      handleDateSelectDirect(date);
    },
    [isEditing, hasChanges, cancelEdit, handleDateSelectDirect],
  );

  const handleConfirmDateChange = useCallback(() => {
    cancelEdit();
    const date = pendingNav?.type === 'date' ? pendingNav.date : null;
    setPendingNav(null);
    handleDateSelectDirect(date);
  }, [cancelEdit, pendingNav, handleDateSelectDirect]);

  const handleCancelDateChange = useCallback(() => {
    setPendingNav(null);
  }, []);

  return (
    <BaseFilterSidebar title="Recall Report">
      {/* Inline calendar */}
      <RecallReportCalendar
        availableDates={availableDates}
        selectedDate={selectedDate}
        onDateSelect={handleDateSelect}
        hasTodayReport={hasTodayReport}
      />

      {/* Feed Rules */}
      <div className="space-y-2">
        <h3 className="text-sm font-medium text-gray-900">Active rules</h3>
        <div className="space-y-1.5">
          {FEED_RULES.map((rule) => (
            <div key={rule} className="flex items-center gap-2">
              <span className="text-green-600 text-sm">&#10003;</span>
              <span className="text-sm text-gray-700">{rule}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Skip List - Coming Soon */}
      <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-4 opacity-50">
        <div className="flex items-center gap-2 mb-2">
          <ListBulletIcon className="h-4 w-4 text-gray-400" />
          <h3 className="text-sm font-medium text-gray-400">Skip List</h3>
          <span className="text-[10px] font-medium text-gray-400 bg-gray-200 rounded px-1.5 py-0.5">
            Coming Soon
          </span>
        </div>
        <p className="text-xs text-gray-400">
          Manage tickers excluded from future reports
        </p>
      </div>

      <ConfirmDialog
        show={showUnsavedConfirm}
        title="Unsaved Changes"
        message="You have unsaved changes. Navigating away will discard them. Continue?"
        confirmLabel="Discard Changes"
        cancelLabel="Stay"
        variant="danger"
        onConfirm={handleConfirmDateChange}
        onCancel={handleCancelDateChange}
      />
    </BaseFilterSidebar>
  );
};
